const BYBIT_API_BASE = 'https://api.bybit.com';
export const BYBIT_CATEGORY_FALLBACKS = ['linear', 'spot', 'inverse'];
const BYBIT_PUBLIC_WS_BASE = 'wss://stream.bybit.com/v5/public';
const BYBIT_PUBLIC_WS_PATHS = {
  linear: 'linear',
  spot: 'spot',
  inverse: 'inverse'
};

const buildBybitUrl = (path, params = {}) => {
  const url = new URL(path, BYBIT_API_BASE);

  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    url.searchParams.set(key, String(value));
  });

  return url.toString();
};

const fetchBybit = async (path, { params, signal } = {}) => {
  const response = await fetch(buildBybitUrl(path, params), {
    method: 'GET',
    signal,
    headers: { Accept: 'application/json' }
  });

  let payload = null;
  try {
    payload = await response.json();
  } catch (error) {
    payload = null;
  }

  if (!response.ok) {
    throw new Error(payload?.retMsg || `Bybit request failed (${response.status})`);
  }

  if (payload?.retCode !== 0) {
    throw new Error(payload?.retMsg || 'Bybit request failed');
  }

  return payload.result;
};

export const resolveBybitInstrument = async (symbol, { signal, categories = BYBIT_CATEGORY_FALLBACKS } = {}) => {
  const normalizedSymbol = String(symbol || '').trim().toUpperCase();
  if (!normalizedSymbol) {
    throw new Error('Bybit symbol is required');
  }

  let lastError = null;

  for (const category of categories) {
    try {
      const result = await fetchBybit('/v5/market/instruments-info', {
        params: { category, symbol: normalizedSymbol },
        signal
      });

      const instrument = Array.isArray(result?.list)
        ? result.list.find((item) => item.symbol === normalizedSymbol)
        : null;

      if (instrument) {
        return { category, instrument };
      }
    } catch (error) {
      lastError = error;
    }
  }

  throw lastError || new Error(`Bybit instrument ${normalizedSymbol} not found`);
};

export const buildBybitPublicWebSocketUrl = (category = 'linear') => {
  const normalizedCategory = String(category || '').trim().toLowerCase();
  const streamPath = BYBIT_PUBLIC_WS_PATHS[normalizedCategory];

  if (!streamPath) {
    throw new Error(`Unsupported Bybit public WebSocket category: ${category}`);
  }

  return `${BYBIT_PUBLIC_WS_BASE}/${streamPath}`;
};

export const fetchBybitOrderbook = async ({ symbol, category = 'linear', limit = 8, signal }) => {
  const normalizedSymbol = String(symbol || '').trim().toUpperCase();
  const result = await fetchBybit('/v5/market/orderbook', {
    params: { category, symbol: normalizedSymbol, limit },
    signal
  });

  return {
    symbol: result?.s || normalizedSymbol,
    bids: Array.isArray(result?.b) ? result.b : [],
    asks: Array.isArray(result?.a) ? result.a : [],
    timestamp: Number(result?.ts || Date.now()),
    updateId: Number(result?.u || 0),
    sequence: Number(result?.seq || 0)
  };
};
